"use client"

import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useMemo, useState } from "react"
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  Gamepad2,
  Hash,
  LayoutGrid,
  Loader2,
  LogIn,
  Plus,
  Search,
  Shield,
  Swords,
  Target,
  Trophy,
  UsersRound,
  X,
} from "lucide-react"
import { Footer } from "@/components/layout/footer"
import { Header } from "@/components/layout/header"
import { GameLibraryArtwork } from "@/components/game/game-library-artwork"
import { TurnstileWidget } from "@/components/security/turnstile-widget"
import { trackProductEvent } from "@/lib/analytics/client"
import { VERSUS_MAX_PLAYERS } from "@/lib/versus-config"
import styles from "./versus.module.css"

interface GameType {
  id: string
  name: string
  slug: string
  category: {
    name: string
    slug: string
  }
  subtypes: Array<{
    id: string
    name: string
    slug: string
  }>
  _count: {
    items: number
  }
}

const TIME_OPTIONS = [
  { value: null, label: "None" },
  { value: 5, label: "5s" },
  { value: 10, label: "10s" },
  { value: 15, label: "15s" },
  { value: 30, label: "30s" },
] as const

const POPULAR_GAMES = [
  {
    href: "/play/sports/nba-salaries",
    label: "NBA Higher or Lower",
    icon: Trophy,
  },
  {
    href: "/play/technology/google-searches",
    label: "Higher or Lower Google Searches",
    icon: Search,
  },
  {
    href: "/play/sports/football-market-value",
    label: "Higher or Lower Football",
    icon: Target,
  },
  {
    href: "/play/gaming/pokemon-card-prices",
    label: "Pokémon Card Prices",
    icon: Gamepad2,
  },
] as const

type GamePickerStep = "closed" | "categories" | "games"

export default function VersusPage() {
  const router = useRouter()
  const [gameTypes, setGameTypes] = useState<GameType[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)

  const [selectedGameType, setSelectedGameType] = useState("")
  const [selectedSubtype, setSelectedSubtype] = useState("")
  const [gamePickerStep, setGamePickerStep] =
    useState<GamePickerStep>("closed")
  const [activeCategorySlug, setActiveCategorySlug] = useState("")
  const [itemCount, setItemCount] = useState(10)
  const [timeLimit, setTimeLimit] = useState<number | null>(null)
  const [hostName, setHostName] = useState("")
  const [gameMode, setGameMode] = useState<"knockout" | "bestof">("bestof")
  const [joinCode, setJoinCode] = useState("")
  const [joinCodeError, setJoinCodeError] = useState("")

  useEffect(() => {
    fetch("/api/versus")
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load game types")
        return response.json()
      })
      .then((data: GameType[]) => {
        setGameTypes(data)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load game types")
        setLoading(false)
      })
  }, [])

  const selectedGame = gameTypes.find(
    (gameType) => gameType.id === selectedGameType,
  )
  const gameCategories = useMemo(() => {
    const categories = new Map<
      string,
      { name: string; slug: string; games: GameType[] }
    >()

    for (const gameType of gameTypes) {
      const current = categories.get(gameType.category.slug)
      if (current) {
        current.games.push(gameType)
      } else {
        categories.set(gameType.category.slug, {
          name: gameType.category.name,
          slug: gameType.category.slug,
          games: [gameType],
        })
      }
    }

    return Array.from(categories.values())
      .map((category) => ({
        ...category,
        games: category.games.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [gameTypes])
  const activeCategory = gameCategories.find(
    (category) => category.slug === activeCategorySlug,
  )
  const maxItems = selectedGame
    ? Math.max(5, Math.min(selectedGame._count.items - 1, 50))
    : 50

  const openGamePicker = () => {
    setActiveCategorySlug("")
    setGamePickerStep("categories")
  }

  const selectGame = (gameType: GameType) => {
    const nextMax = Math.max(5, Math.min(gameType._count.items - 1, 50))
    setSelectedGameType(gameType.id)
    setSelectedSubtype("")
    setItemCount((count) => Math.min(count, nextMax))
    setActiveCategorySlug(gameType.category.slug)
    setGamePickerStep("closed")
    if (error) setError("")
  }

  const selectCategory = (
    category: (typeof gameCategories)[number],
  ) => {
    if (category.games.length === 1) {
      selectGame(category.games[0])
      return
    }

    setActiveCategorySlug(category.slug)
    setGamePickerStep("games")
  }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How do you play Higher Lower multiplayer?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Create a room, choose the game mode and rounds, share your code, and start once everyone joins. All players get the same prompts and compete for the best score.",
        },
      },
      {
        "@type": "Question",
        name: "How many players can join versus mode?",
        acceptedAnswer: {
          "@type": "Answer",
          text: `A room supports up to ${VERSUS_MAX_PLAYERS} players. The host does not choose a player count and can start as soon as at least two people have joined.`,
        },
      },
      {
        "@type": "Question",
        name: "Is Higher Lower multiplayer free?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Versus mode is free to play in your browser with no app download required.",
        },
      },
    ],
  }

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://playhigherlower.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Higher or Lower Multiplayer",
        item: "https://playhigherlower.com/versus",
      },
    ],
  }

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!hostName.trim()) {
      setError("Please enter your name")
      return
    }
    if (!selectedGameType) {
      setError("Please select a game")
      return
    }

    setCreating(true)
    setError("")

    try {
      const response = await fetch("/api/versus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gameTypeId: selectedGameType,
          subtypeId: selectedSubtype || null,
          itemCount,
          timeLimit,
          gameMode,
          hostName: hostName.trim(),
          turnstileToken,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to create game")
      }

      const game = await response.json()
      const hostPlayer = game.players.find(
        (player: { isHost: boolean }) => player.isHost,
      )
      if (hostPlayer) {
        localStorage.setItem(`versus_${game.code}`, hostPlayer.id)
      }
      trackProductEvent("versus_room_created", {
        gameTypeId: selectedGameType,
        properties: {
          itemCount,
          roomCapacity: VERSUS_MAX_PLAYERS,
          gameMode,
          timeLimit,
        },
      })
      router.push(`/versus/${game.code}`)
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Failed to create game",
      )
      setCreating(false)
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    }
  }

  const handleJoinCode = (event: React.FormEvent) => {
    event.preventDefault()
    const normalizedCode = joinCode
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")

    if (normalizedCode.length !== 6) {
      setJoinCodeError("Enter the 6-character room code")
      return
    }

    setJoinCodeError("")
    router.push(`/versus/${normalizedCode}`)
  }

  return (
    <div className={styles.page}>
      <Header />

      <main>
        <section className={styles.hero} aria-labelledby="versus-title">
          <div className={styles.heroCopy}>
            <div className={styles.eyebrow}>
              <span aria-hidden="true">
                <Swords />
              </span>
              Versus mode
            </div>
            <h1 id="versus-title">
              Higher or Lower
              <strong>Multiplayer</strong>
            </h1>
            <p>
              Create a room, invite your friends, and start a head-to-head
              battle. Highest score wins!
            </p>
          </div>

          <div className={styles.heroVisual} aria-hidden="true">
            <span className={styles.heroHalo} />
            <Image
              src="/images/versus/versus-hero.webp"
              alt=""
              width={1537}
              height={1023}
              priority
              sizes="(max-width: 760px) 92vw, 620px"
            />
          </div>
        </section>

        <section className={styles.workspace} aria-label="Multiplayer room setup">
          <div className={styles.builder}>
            <form className={styles.joinForm} onSubmit={handleJoinCode}>
              <div className={styles.sectionHeading}>
                <h2>Join a room</h2>
                <p>Enter a room code to join an existing game.</p>
              </div>

              <div className={styles.joinRow}>
                <div className={styles.field}>
                  <label className={styles.srOnly} htmlFor="room-code">
                    Six-character room code
                  </label>
                  <input
                    id="room-code"
                    value={joinCode}
                    onChange={(event) => {
                      setJoinCode(event.target.value.toUpperCase())
                      setJoinCodeError("")
                    }}
                    placeholder="ABC123"
                    maxLength={6}
                    autoComplete="off"
                    spellCheck={false}
                    className={styles.roomCodeInput}
                  />
                </div>
                <button className={styles.primaryButton} type="submit">
                  Join room
                  <span className={styles.ctaIcon} aria-hidden="true">
                    <LogIn />
                  </span>
                </button>
              </div>
              {joinCodeError ? (
                <p className={styles.error} role="alert">
                  {joinCodeError}
                </p>
              ) : null}
            </form>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <div className={styles.sectionHeading}>
              <h2>Create your game</h2>
            </div>

            {loading ? (
              <div className={styles.loadingState} role="status">
                <Loader2 aria-hidden="true" />
                Loading games
              </div>
            ) : (
              <form className={styles.createForm} onSubmit={handleCreate}>
                <div className={styles.field}>
                  <label htmlFor="host-name">Your name</label>
                  <input
                    id="host-name"
                    type="text"
                    value={hostName}
                    onChange={(event) => {
                      setHostName(event.target.value)
                      if (error) setError("")
                    }}
                    placeholder="Enter your name"
                    maxLength={20}
                    autoComplete="nickname"
                  />
                  <p>This is how other players will see you.</p>
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel} id="versus-game-label">
                    Game
                  </span>
                  <div className={styles.gamePicker}>
                    {gamePickerStep === "closed" ? (
                      <button
                        type="button"
                        className={styles.gamePickerSummary}
                        onClick={openGamePicker}
                        aria-labelledby="versus-game-label game-picker-value"
                        aria-expanded={false}
                        aria-controls="versus-game-picker-panel"
                      >
                        <span className={styles.gamePickerSummaryIcon}>
                          {selectedGame ? (
                            <GameLibraryArtwork
                              categorySlug={selectedGame.category.slug}
                              gameSlug={selectedGame.slug}
                            />
                          ) : (
                            <Gamepad2 aria-hidden="true" />
                          )}
                        </span>
                        <span
                          className={styles.gamePickerSummaryCopy}
                          aria-live="polite"
                        >
                          <small>
                            {selectedGame
                              ? selectedGame.category.name
                              : "Choose a category first"}
                          </small>
                          <strong id="game-picker-value">
                            {selectedGame?.name ?? "Select a game"}
                          </strong>
                          <span>
                            {selectedGame
                              ? `${selectedGame._count.items} items available`
                              : `${gameCategories.length} categories available`}
                          </span>
                        </span>
                        <span className={styles.gamePickerAction}>
                          {selectedGame ? "Change" : "Choose"}
                          <LayoutGrid aria-hidden="true" />
                        </span>
                      </button>
                    ) : (
                      <div
                        className={styles.gamePickerPanel}
                        id="versus-game-picker-panel"
                        aria-labelledby="game-picker-heading"
                      >
                        <div className={styles.gamePickerHeader}>
                          <div>
                            {gamePickerStep === "games" ? (
                              <button
                                type="button"
                                className={styles.gamePickerBack}
                                onClick={() => {
                                  setActiveCategorySlug("")
                                  setGamePickerStep("categories")
                                }}
                              >
                                <ArrowLeft aria-hidden="true" />
                                All categories
                              </button>
                            ) : (
                              <span>Game library</span>
                            )}
                            <h3 id="game-picker-heading">
                              {gamePickerStep === "games"
                                ? activeCategory?.name
                                : "Choose a category"}
                            </h3>
                            <p>
                              {gamePickerStep === "games"
                                ? "Now choose the game you want to play."
                                : "Pick a topic to see its available games."}
                            </p>
                          </div>
                          <button
                            type="button"
                            className={styles.gamePickerClose}
                            onClick={() => setGamePickerStep("closed")}
                            aria-label="Close game picker"
                          >
                            <X aria-hidden="true" />
                          </button>
                        </div>

                        {gamePickerStep === "categories" ? (
                          <div
                            className={styles.categoryGrid}
                            role="group"
                            aria-label="Game categories"
                          >
                            {gameCategories.map((category) => (
                              <button
                                key={category.slug}
                                type="button"
                                onClick={() => selectCategory(category)}
                              >
                                <span
                                  className={styles.categoryIcon}
                                  aria-hidden="true"
                                >
                                  <GameLibraryArtwork
                                    categorySlug={category.slug}
                                  />
                                </span>
                                <span>
                                  <strong>{category.name}</strong>
                                  <small>
                                    {category.games.length === 1
                                      ? category.games[0].name
                                      : `${category.games.length} games`}
                                  </small>
                                </span>
                                <ChevronRight aria-hidden="true" />
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div
                            className={styles.gameOptionList}
                            role="group"
                            aria-label={`${activeCategory?.name ?? ""} games`}
                          >
                            {activeCategory?.games.map((gameType) => {
                              const isSelected =
                                gameType.id === selectedGameType

                              return (
                                <button
                                  key={gameType.id}
                                  type="button"
                                  aria-pressed={isSelected}
                                  onClick={() => selectGame(gameType)}
                                >
                                  <span
                                    className={styles.gameOptionIcon}
                                    aria-hidden="true"
                                  >
                                    <GameLibraryArtwork
                                      categorySlug={gameType.category.slug}
                                      gameSlug={gameType.slug}
                                    />
                                  </span>
                                  <span>
                                    <strong>{gameType.name}</strong>
                                    <small>
                                      {gameType._count.items} items available
                                    </small>
                                  </span>
                                  <span
                                    className={styles.gameOptionState}
                                    aria-hidden="true"
                                  >
                                    {isSelected ? <Check /> : <Plus />}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {selectedGame && selectedGame.subtypes.length > 0 ? (
                  <div className={styles.field}>
                    <label htmlFor="versus-subtype">Filter (optional)</label>
                    <div className={styles.selectWrap}>
                      <Target aria-hidden="true" />
                      <select
                        id="versus-subtype"
                        value={selectedSubtype}
                        onChange={(event) =>
                          setSelectedSubtype(event.target.value)
                        }
                      >
                        <option value="">All items</option>
                        {selectedGame.subtypes.map((subtype) => (
                          <option key={subtype.id} value={subtype.id}>
                            {subtype.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown aria-hidden="true" />
                    </div>
                  </div>
                ) : null}

                <div className={styles.settingsGrid}>
                  <fieldset className={styles.roundsField}>
                    <legend>
                      <Hash aria-hidden="true" />
                      Rounds
                    </legend>
                    <div className={styles.rangeRow}>
                      <div className={styles.rangeControl}>
                        <input
                          type="range"
                          min={5}
                          max={maxItems}
                          value={Math.min(itemCount, maxItems)}
                          onChange={(event) =>
                            setItemCount(Number(event.target.value))
                          }
                          aria-label="Number of rounds"
                        />
                        <div aria-hidden="true">
                          <span>5</span>
                          <span>{maxItems}</span>
                        </div>
                      </div>
                      <output aria-label={`${itemCount} rounds`}>
                        {itemCount}
                      </output>
                    </div>
                  </fieldset>

                  <fieldset className={styles.timeField}>
                    <legend>
                      <Clock3 aria-hidden="true" />
                      Time per round
                    </legend>
                    <div
                      className={styles.timeOptions}
                      role="group"
                      aria-label="Time per round"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => setTimeLimit(option.value)}
                          aria-pressed={timeLimit === option.value}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {timeLimit !== null ? (
                      <div className={styles.customTime}>
                        <input
                          type="range"
                          min={3}
                          max={60}
                          value={timeLimit}
                          onChange={(event) =>
                            setTimeLimit(Number(event.target.value))
                          }
                          aria-label="Custom seconds per round"
                        />
                        <output>{timeLimit}s</output>
                      </div>
                    ) : null}
                  </fieldset>
                </div>

                <fieldset className={styles.modeField}>
                  <legend>
                    <Swords aria-hidden="true" />
                    Game mode
                  </legend>
                  <div className={styles.modeGrid}>
                    <button
                      type="button"
                      onClick={() => setGameMode("knockout")}
                      aria-pressed={gameMode === "knockout"}
                    >
                      <Shield aria-hidden="true" />
                      <span>
                        <strong>Knockout</strong>
                        <small>One wrong answer and you&apos;re out.</small>
                      </span>
                      <span className={styles.modeCheck} aria-hidden="true">
                        {gameMode === "knockout" ? <Check /> : null}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGameMode("bestof")}
                      aria-pressed={gameMode === "bestof"}
                    >
                      <Trophy aria-hidden="true" />
                      <span>
                        <strong>Full Match</strong>
                        <small>
                          Everyone plays every round. Highest score wins!
                        </small>
                      </span>
                      <span className={styles.modeCheck} aria-hidden="true">
                        {gameMode === "bestof" ? <Check /> : null}
                      </span>
                    </button>
                  </div>
                </fieldset>

                <div className={styles.verification}>
                  <TurnstileWidget
                    onTokenChange={setTurnstileToken}
                    resetKey={turnstileResetKey}
                    theme="light"
                  />
                </div>

                {error ? (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={creating || !hostName.trim() || !turnstileToken}
                  className={styles.createButton}
                >
                  {creating ? (
                    <>
                      <Loader2
                        className={styles.spinner}
                        aria-hidden="true"
                      />
                      Creating game
                    </>
                  ) : (
                    <>
                      Create game
                      <span className={styles.ctaIcon} aria-hidden="true">
                        <Plus />
                      </span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <aside className={styles.rail}>
            <section className={styles.infoPanel}>
              <h2 className={styles.srOnly}>Why play with friends</h2>
              <ul className={styles.benefits}>
                <li>
                  <span aria-hidden="true">
                    <UsersRound />
                  </span>
                  <div>
                    <strong>Invite friends</strong>
                    <p>
                      Share the room code with as many friends as you want.
                    </p>
                  </div>
                </li>
                <li>
                  <span aria-hidden="true">
                    <Target />
                  </span>
                  <div>
                    <strong>Real-time play</strong>
                    <p>Everyone guesses at the same time, every round.</p>
                  </div>
                </li>
                <li>
                  <span aria-hidden="true">
                    <CalendarDays />
                  </span>
                  <div>
                    <strong>Track the score</strong>
                    <p>Watch the leaderboard update live as you play.</p>
                  </div>
                </li>
              </ul>

              <div className={styles.railDivider} />

              <h2>How to play</h2>
              <ol className={styles.steps}>
                <li>
                  <span>1</span>
                  Create a room and set your game, rounds, and time limit.
                </li>
                <li>
                  <span>2</span>
                  Share the room code and wait for players to join.
                </li>
                <li>
                  <span>3</span>
                  Start the game and guess Higher or Lower each round.
                </li>
              </ol>
            </section>

            <section className={styles.popularPanel}>
              <h2>Popular game picks</h2>
              <ul>
                {POPULAR_GAMES.map((game) => {
                  const Icon = game.icon
                  return (
                    <li key={game.href}>
                      <Link href={game.href}>
                        <span aria-hidden="true">
                          <Icon />
                        </span>
                        {game.label}
                        <ChevronRight aria-hidden="true" />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </section>
          </aside>
        </section>

        <section className={styles.faq} aria-labelledby="multiplayer-faq">
          <div className={styles.faqContent}>
            <h2 id="multiplayer-faq">
              <span aria-hidden="true">
                <UsersRound />
              </span>
              Multiplayer FAQ
            </h2>
            <dl>
              <div>
                <dt>How many players can join?</dt>
                <dd>Up to {VERSUS_MAX_PLAYERS} players can join a room.</dd>
              </div>
              <div>
                <dt>Can I play with a timer?</dt>
                <dd>Yes, set a time limit per round or play without one.</dd>
              </div>
              <div>
                <dt>Do all players get the same questions?</dt>
                <dd>Yes, everyone sees the same items in the same order.</dd>
              </div>
            </dl>
          </div>
          <div className={styles.faqVisual} aria-hidden="true">
            <span className={styles.faqShapeOne} />
            <span className={styles.faqShapeTwo} />
            <Image
              src="/images/versus/versus-players.webp"
              alt=""
              width={1691}
              height={930}
              sizes="(max-width: 760px) 70vw, 360px"
            />
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([breadcrumbJsonLd, faqJsonLd]),
          }}
        />
      </main>

      <Footer />
    </div>
  )
}
